import { Calendar, ChevronRight, Folder, User, Users } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useData, useMutation } from '../../Shared/api';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { Campaign, CampaignType, Event } from '../../Shared/types';
import { Button } from '../../Shared/UI/Button';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { Icon } from '../../Shared/UI/Icon';
import { Input } from '../../Shared/UI/Input';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import Picker, { PickerItem } from '../../Shared/UI/Picker';
import { Radio } from '../../Shared/UI/Radio';
import { RadioBox } from '../../Shared/UI/RadioBox';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { useRouter } from '../router';

export default function AddCampaignForm({
  projects,
  onSuccess,
}: {
  projects: PickerItem[];
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const {
    register, handleSubmit, setError, watch, control, setValue,
    formState: { errors, defaultValues },
  } = useForm({
    defaultValues: {
      project_id: projects.length === 1 ? projects[0].id : null,
      event_id: null as string | null,
      email: '',
      first_name: '',
      last_name: '',
      title: '',
      type: 'individual',
      team_id: null,
      enabled: false,
    },
  });

  const projectId = watch('project_id');
  const eventId = watch('event_id');
  const isTeam = watch('type') === 'team';

  const [teams] = useData<Campaign[]>(
    projectId ? routes.project_campaigns_json(projectId) : null,
    { params: { type: CampaignType.Team, event_id: eventId } },
  );

  const [events] = useData<Event[]>(
    projectId ? routes.project_events_json(projectId) : null,
    {
      onSuccess: (events) => {
        if (events.length === 1) {
          // Preselect the event if there is exactly 1.
          setValue('event_id', events[0].id);
        } else if (! events.some((event) => event.id === eventId)) {
          // Unset the event if it doesn't exist in the list.
          setValue('event_id', null);
        }
      },
    }
  );

  const firstName = watch('first_name');
  const lastName = watch('last_name');

  const [addCampaign, { loading, errors: serverErrors }] = useMutation(routes.add_campaign(), {
    onSuccess: () => {
      notifier.notify(t('backend:campaign_created'));
      onSuccess?.();
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  return (
    <form onSubmit={handleSubmit(addCampaign)}>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="py-1">
            {t('backend:add_a_campaign')}
          </h3>
          <p className="text-slate-700">
            {t('backend:add_campaign_intro')}
          </p>
        </div>

        <hr />

        {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

        <InputGroup>
          <InputLabel required valid={!errors.type} htmlFor="add_campaign_type_individual">
            {t('backend:campaign_type')}
          </InputLabel>
          <div className="space-y-2">
            <RadioBox>
              <div className="mr-4">
                <Radio
                  {...register('type', { required: true })}
                  value={CampaignType.Individual}
                  id="add_campaign_type_individual"
                  aria-invalid={!!errors.type}
                />
              </div>
              <div>
                <strong className="block">
                  <Icon className="mr-1">
                    <User />
                  </Icon>
                  {t('backend:campaign_type_individual')}
                </strong>
                {t('backend:campaign_type_individual_description')}
              </div>
            </RadioBox>
            <RadioBox>
              <div className="mr-4">
                <Radio
                  {...register('type', { required: true })}
                  value={CampaignType.Team}
                  id="add_campaign_type_team"
                  aria-invalid={!!errors.type}
                />
              </div>
              <div>
                <strong className="block">
                  <Icon className="mr-1">
                    <Users />
                  </Icon>
                  {t('backend:campaign_type_team')}
                </strong>
                {t('backend:campaign_type_team_description')}
              </div>
            </RadioBox>
            <ErrorMessage attribute={t('backend:type')} error={errors.type} />
          </div>
        </InputGroup>

        <InputGroup>
          <InputLabel required valid={!errors.project_id} htmlFor="add_campaign_project">
            {t('backend:project')}
          </InputLabel>
          <Controller
            control={control}
            name="project_id"
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Picker
                selected={value}
                items={projects}
                onChange={(value) => {
                  onChange(value?.id || null);
                  setValue('team_id', null);
                }}
                onBlur={onBlur}
                icon={<Folder />}
                aria-invalid={!!errors.project_id}
                id="add_campaign_project"
              />
            )}
          />
          <ErrorMessage attribute={t('backend:project')} error={errors.project_id} />
        </InputGroup>

        {events && events.length > 0 && (
          <InputGroup>
            <InputLabel required valid={!errors.event_id} htmlFor="add_campaign_event">
              {t('backend:event')}
            </InputLabel>
            <Controller
              control={control}
              name="event_id"
              render={({ field: { onChange, onBlur, value } }) => (
                <Picker
                  selected={value}
                  items={events}
                  onChange={(value) => {
                    onChange(value?.id || null);
                    setValue('team_id', null);
                  }}
                  onBlur={onBlur}
                  icon={<Calendar />}
                  aria-invalid={!!errors.event_id}
                  id="add_campaign_event"
                />
              )}
            />
            <ErrorMessage attribute={t('backend:event')} error={errors.event_id} />
          </InputGroup>
        )}

        {teams && teams.length > 0 && !isTeam && (
          <InputGroup>
            <InputLabel valid={!errors.team_id} htmlFor="add_campaign_team">
              {t('backend:team')}
            </InputLabel>
            <Controller
              control={control}
              name="team_id"
              render={({ field: { onChange, onBlur, value } }) => (
                <Picker
                  selected={value}
                  items={teams}
                  onChange={(value) => onChange(value?.id || null)}
                  onBlur={onBlur}
                  icon={<Users />}
                  aria-invalid={!!errors.team_id}
                  placeholder={t('backend:no_team')}
                  id="add_campaign_team"
                  optional
                />
              )}
            />
            <ErrorMessage attribute={t('backend:team')} error={errors.team_id} />
          </InputGroup>
        )}

        <InputGroup>
          <InputLabel required valid={!errors.email} htmlFor="add_campaign_email">
            {t('backend:email_address')}
          </InputLabel>
          <Input
            {...register('email', { required: true })} inputMode="email"
            id="add_campaign_email"
            aria-invalid={!!errors.email}
          />
          <ErrorMessage attribute={t('backend:email_address')} error={errors.email }/>
        </InputGroup>

        <div className="grid grid-cols-2 gap-6">
          <InputGroup>
            <InputLabel required valid={!errors.first_name} htmlFor="add_campaign_first_name">
              {t('backend:first_name')}
            </InputLabel>
            <Input
              {...register('first_name', { required: true })}
              id="add_campaign_first_name"
              aria-invalid={!!errors.first_name}
            />
            <ErrorMessage attribute={t('backend:first_name')} error={errors.first_name }/>
          </InputGroup>

          <InputGroup>
            <InputLabel required valid={!errors.last_name} htmlFor="add_campaign_last_name">
              {t('backend:last_name')}
            </InputLabel>
            <Input
              {...register('last_name', { required: true })}
              id="add_campaign_last_name"
              aria-invalid={!!errors.last_name}
            />
            <ErrorMessage attribute={t('backend:last_name')} error={errors.last_name }/>
          </InputGroup>
        </div>

        <InputGroup>
          <InputLabel htmlFor="add_campaign_title">
            {t('backend:campaign_title')}
          </InputLabel>
          <Input
            {...register('title')}
            id="add_campaign_title"
            placeholder={`${firstName} ${lastName}`}
            aria-invalid={!!errors.title}
          />
          <ErrorMessage error={errors.title} attribute={t('backend:campaign_title')} />
        </InputGroup>

        <hr />

        <div className="text-right">
          <Button
            type="submit"
            disabled={loading}
            loading={loading}
          >
            {t('backend:continue')}
            <Icon className="ml-1">
              <ChevronRight />
            </Icon>
          </Button>
        </div>
      </div>
    </form>
  );
}
